import React from 'react';
import { Link } from 'react-router-dom';
import { Cross, AlertTriangle, Clock, ChefHat } from 'lucide-react';
import { recipes } from '../data/mock';
import ItemPhoto from '../components/ItemPhoto';

const conditions = [
  { key: 'bee sting', label: 'Bee Stings', match: ['bee sting', 'stings'] },
  { key: 'insect bites', label: 'Insect Bites', match: ['insect bites', 'bug bites', 'bites', 'mosquitos', 'bugs'] },
  { key: 'headache', label: 'Headaches', match: ['headache', 'migraine'] },
  { key: 'muscle pain', label: 'Muscle Pain', match: ['muscle pain', 'soreness'] },
  { key: 'bleeding', label: 'Minor Bleeding', match: ['bleeding'] },
  { key: 'cough', label: 'Coughs', match: ['cough'] },
  { key: 'fever', label: 'Fever', match: ['fever'] },
  { key: 'scrapes', label: 'Scrapes', match: ['scrapes'] },
  { key: 'cuts', label: 'Cuts', match: ['cuts'] },
  { key: 'burns', label: 'Burns', match: ['burns', 'minor burns', 'sunburn'] },
];

function recipesFor(match) {
  const set = new Map();
  recipes.forEach((r) => {
    if (r.symptoms.some((s) => match.includes(s.toLowerCase()))) set.set(r.id, r);
  });
  return [...set.values()];
}

export default function FirstAidKit() {
  return (
    <div className="max-w-6xl mx-auto px-5 py-10 min-h-[70vh]" data-testid="first-aid-kit-page">
      <div className="mb-4">
        <h1 className="font-serif text-4xl text-botanica-ink flex items-center gap-2">
          <Cross className="w-7 h-7 text-botanica-green" /> First Aid Kit
        </h1>
        <p className="text-botanica-muted mt-2 max-w-2xl text-sm">
          A gentle, browsable herbal reference for everyday scrapes and mishaps. Tap any remedy for ingredients and steps.
        </p>
      </div>

      <div className="flex items-start gap-3 bg-amber-50 rounded-2xl p-4 mb-8 max-w-3xl" data-testid="first-aid-emergency-note">
        <AlertTriangle className="w-5 h-5 text-botanica-warn mt-0.5 shrink-0" />
        <p className="text-sm text-botanica-warn">
          Educational only — not a substitute for medical care. For deep or heavy bleeding, serious burns, difficulty breathing,
          or any emergency, call your local emergency number right away.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-10">
        {conditions.map((c) => (
          <a key={c.key} href={`#${c.key.replace(/\s+/g, '-')}`} className="text-sm px-3 py-1.5 rounded-full bg-botanica-card/60 text-botanica-muted hover:text-botanica-ink hover:bg-botanica-card transition-colors" data-testid={`first-aid-chip-${c.key.replace(/\s+/g, '-')}`}>
            {c.label}
          </a>
        ))}
      </div>

      <div className="space-y-12">
        {conditions.map((c) => {
          const list = recipesFor(c.match);
          if (list.length === 0) return null;
          return (
            <section key={c.key} id={c.key.replace(/\s+/g, '-')} className="scroll-mt-24" data-testid={`first-aid-section-${c.key.replace(/\s+/g, '-')}`}>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="font-serif text-2xl text-botanica-ink">{c.label}</h2>
                <span className="text-xs text-botanica-muted">{list.length} remed{list.length === 1 ? 'y' : 'ies'}</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {list.map((r) => (
                  <Link
                    key={r.id}
                    to={`/recipes/${r.id}`}
                    className="group bg-botanica-card/70 hover:bg-botanica-card rounded-2xl overflow-hidden transition-colors"
                    data-testid={`first-aid-recipe-${r.id}`}
                  >
                    <ItemPhoto itemId={r.id} alt={r.name} className="w-full h-36" />
                    <div className="p-4">
                      <p className="text-[10px] uppercase tracking-widest text-botanica-green font-semibold">{r.type}</p>
                      <h3 className="font-serif text-lg text-botanica-ink leading-tight group-hover:text-botanica-greenDark transition-colors">{r.name}</h3>
                      <div className="flex items-center gap-3 text-xs text-botanica-muted mt-2">
                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {r.time}</span>
                        <span className="flex items-center gap-1 capitalize"><ChefHat className="w-3.5 h-3.5" /> {r.difficulty}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
